import React from 'react';
import { statsService, GameStats, ParScoreDistribution, SingleProposalStats } from './services/stats';
import TopNavigation from './TopNavigation';
import ClueRaiderBanner from './components/ClueRaiderBanner';
import MailerLiteSignup from './components/MailerLiteSignup';
import tiktokIcon from './TikTok_Icon_Black_Circle.png';
import { updatePageSEO } from './utils/seo';

const Stats: React.FC = () => {
  const [stats, setStats] = React.useState<GameStats | null>(null);
  const [singleProposalStats, setSingleProposalStats] = React.useState<SingleProposalStats | null>(null);
  const [distribution, setDistribution] = React.useState<ParScoreDistribution>({});
  const [isSingleProposalMode, setIsSingleProposalMode] = React.useState<boolean>(true);

  // Update SEO
  React.useEffect(() => {
    updatePageSEO(
      'Dimension Flux Statistics - Your Puzzle Solving Stats',
      'View your Dimension Flux puzzle solving statistics: track your progress, see your solve rates, and review your performance across different difficulty levels.'
    );
  }, []);

  React.useEffect(() => {
    // Check current game mode from localStorage
    try {
      const savedState = localStorage.getItem('dimension23_game_state_v2');
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        setIsSingleProposalMode(parsedState.newGameMode !== undefined ? parsedState.newGameMode : true);
      } else {
        // No saved state, default to single proposal mode
        setIsSingleProposalMode(true);
      }
    } catch (error) {
      console.warn('Failed to read game mode from localStorage:', error);
      // Default to single proposal mode on error
      setIsSingleProposalMode(true);
    }

    // Load appropriate stats
    setStats(statsService.getStats());
    setSingleProposalStats(statsService.getSingleProposalStats());
    setDistribution(statsService.getParScoreDistribution());
  }, []);

  if (!stats || !singleProposalStats) {
    return <div className="flex justify-center items-center h-screen">Loading stats...</div>;
  }

  const maxCount = Math.max(...Object.values(distribution));

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      {/* Top Navigation */}
      <TopNavigation />
      <ClueRaiderBanner />

      <div className="max-w-xl mx-auto w-full p-2 sm:p-4">

        {/* Stats Content */}
        <div className="bg-white bg-opacity-95 rounded-lg p-3 sm:p-6 border-2 border-gray-300 shadow-lg mb-4 sm:mb-6 w-full">
          <h2 className="text-xl sm:text-2xl font-oswald font-bold text-gray-900 mb-3 sm:mb-4 text-center leading-tight">
            {isSingleProposalMode ? 'SINGLE PROPOSAL STATS' : 'YOUR INVESTIGATION STATS'}
          </h2>
          
          <div className="w-full h-px bg-gray-300 mb-3 sm:mb-4"></div>

          {isSingleProposalMode ? (
            // Single Proposal Mode Stats
            <>
              {/* Streak Information */}
              <div className="mb-6 text-center">
                <p className="text-lg text-gray-800 font-oswald">
                  You're on a <span className="font-bold underline decoration-purple-500 decoration-2">{singleProposalStats.currentStreak}-day streak</span>
                </p>
              </div>

              {/* Key Performance Indicators */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="text-center">
                  <div className="text-base font-oswald font-semibold text-gray-700 mb-1">Total Solved</div>
                  <div className="text-xl font-bold text-gray-900">{singleProposalStats.totalSolved}</div>
                  <div className="text-xs text-gray-600">cases</div>
                </div>
                <div className="text-center">
                  <div className="text-base font-oswald font-semibold text-gray-700 mb-1">Success Rate</div>
                  <div className="text-xl font-bold text-gray-900">{statsService.getSingleProposalSuccessRate()}%</div>
                  <div className="text-xs text-gray-600">accuracy</div>
                </div>
                <div className="text-center">
                  <div className="text-base font-oswald font-semibold text-gray-700 mb-1">Total Attempts</div>
                  <div className="text-xl font-bold text-gray-900">{singleProposalStats.totalAttempts}</div>
                  <div className="text-xs text-gray-600">cases</div>
                </div>
              </div>
            </>
          ) : (
            // Standard Mode Stats
            <>
              {/* Streak Information */}
              <div className="mb-6 text-center">
                <p className="text-lg text-gray-800 font-oswald">
                  You're on a <span className="font-bold underline decoration-purple-500 decoration-2">{stats.currentStreak}-day streak</span>
                </p>
              </div>

              {/* Key Performance Indicators */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="text-center">
                  <div className="text-base font-oswald font-semibold text-gray-700 mb-1">Total Solved</div>
                  <div className="text-xl font-bold text-gray-900">{stats.totalSolved}</div>
                  <div className="text-xs text-gray-600">cases</div>
                </div>
                <div className="text-center">
                  <div className="text-base font-oswald font-semibold text-gray-700 mb-1">Total Score</div>
                  <div className="text-xl font-bold text-gray-900">{stats.totalScore}</div>
                  <div className="text-xs text-gray-600">under par</div>
                </div>
                <div className="text-center">
                  <div className="text-base font-oswald font-semibold text-gray-700 mb-1">Streak Score</div>
                  <div className="text-xl font-bold text-gray-900">{stats.streakScore}</div>
                  <div className="text-xs text-gray-600">under par</div>
                </div>
              </div>
            </>
          )}

          {!isSingleProposalMode && (
            <>
              {/* Par Score Distribution Chart */}
              <div className="mb-6">
                <h2 className="text-lg font-oswald font-semibold text-gray-900 mb-4">Par Score Distribution</h2>
                <div className="space-y-2">
                  {Object.entries(distribution)
                    .sort(([a], [b]) => parseInt(a) - parseInt(b))
                    .map(([score, count]) => (
                      <div key={score} className="flex items-center space-x-3">
                        <div className="w-8 text-sm font-medium text-gray-700">{score}</div>
                        <div className="flex-1">
                          <div 
                            className="h-6 bg-purple-200 rounded"
                            style={{ 
                              width: `${count > 0 ? (count / maxCount) * 100 : 0}%`,
                              minWidth: count > 0 ? '8px' : '0'
                            }}
                          />
                        </div>
                        <div className="w-8 text-sm text-gray-600">{count}</div>
                      </div>
                    ))}
                </div>
              </div>

              {/* Additional Stats */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-md font-oswald font-semibold text-gray-900 mb-3">Additional Stats</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Longest streak:</span>
                    <span className="ml-2 font-medium text-gray-900">{stats.longestStreak} days</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Average par:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {stats.parScores.length > 0 
                        ? (stats.totalScore / stats.parScores.length).toFixed(1)
                        : '0.0'
                      }
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}

          {isSingleProposalMode && (
            /* Additional Stats for Single Proposal Mode */
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-md font-oswald font-semibold text-gray-900 mb-3">Additional Stats</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Longest streak:</span>
                  <span className="ml-2 font-medium text-gray-900">{singleProposalStats.longestStreak} days</span>
                </div>
                <div>
                  <span className="text-gray-600">Failed attempts:</span>
                  <span className="ml-2 font-medium text-gray-900">{singleProposalStats.failedDates.length}</span>
                </div>
              </div>
            </div>
          )}




        </div>

        {/* Newsletter Signup */}
        <div className="mt-6 mb-6">
          <MailerLiteSignup />
        </div>

        {/* Always visible closing message */}
        <div className="text-center mt-6 p-4 bg-gray-100 rounded-lg border border-gray-300">
          <p className="text-lg font-oswald font-semibold text-gray-700">
            Solve today's case. Seal the breach. Return tomorrow. The flux never stops.
          </p>
        </div>

        {/* Social Links */}
        <div className="text-center mt-4 mb-2">
          <a href="https://x.com/Dimension_Flux" target="_blank" rel="noopener noreferrer" className="inline-block mx-2 text-white hover:opacity-80" aria-label="X profile">
            <svg className="w-5 h-5 inline-block align-middle" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M18.244 2H21L13.5 10.5 22.5 22H15.5L10.5 15.5 4.5 22H2L9.5 13.5 1.5 2H8.5L13.5 8.5 18.244 2Z"/>
            </svg>
          </a>
          <a href="https://www.tiktok.com/@dimension_flux" target="_blank" rel="noopener noreferrer" className="inline-block mx-2 hover:opacity-80" aria-label="TikTok profile">
            <img src={tiktokIcon} alt="TikTok" className="w-5 h-5 inline-block align-middle rounded-full" />
          </a>
        </div>

        {/* Copyright + AI Disclosure */}
        <div className="text-center mt-2 text-sm text-white">
          © N.M.A. Pitman 2025 • Contact: <a href="mailto:hello@dimensionflux.com" className="text-white underline">hello@dimensionflux.com</a>
          <div className="mt-1 text-xs opacity-90"><a href="https://www.vecteezy.com/free-vector/portal">Portal Vectors by Vecteezy</a></div>
        </div>

      </div>


    </div>
  );
};

export default Stats;
